import type { AlertDialogOptions } from "@overmap-ai/blocks"

type AlertDialogOpener = (options: AlertDialogOptions) => void

/**
 * The imperative half of the alert dialog, published for callers that are not React components.
 *
 * `useAlertDialog` is a hook, so the confirmation before signing out could only ever live in whichever component
 * happened to render the button -- which is how two of the three sign-out paths came to have no confirmation at all.
 * Registering the opener here lets `MainService.logout` ask the question itself, once, for every caller.
 */
let opener: AlertDialogOpener | undefined

export function setAlertDialogOpener(next: AlertDialogOpener | undefined): void {
	opener = next
}

export type ConfirmationOptions = Omit<AlertDialogOptions, "onAction" | "onCancel" | "onClose">

/**
 * Asks the user to confirm something, from anywhere.
 * @param options Everything `useAlertDialog` takes except the callbacks, which become the resolved value.
 * @returns Whether the user chose the action. Cancelling or dismissing the dialog resolves `false`.
 */
export async function confirm(options: ConfirmationOptions): Promise<boolean> {
	if (!opener) {
		// Before the provider has mounted, or outside it entirely. The native dialog is ugly, but losing the question
		// altogether would mean silently discarding whatever it was guarding.
		console.warn("No alert dialog is mounted; falling back to window.confirm.")
		return window.confirm(`${options.title}\n\n${options.description}`)
	}

	return new Promise<boolean>((resolve) => {
		let settled = false
		const settle = (confirmed: boolean) => {
			// `onClose` fires on every close, including the one that follows the action or cancel button, so the first
			// answer is the real one.
			if (settled) return
			settled = true
			resolve(confirmed)
		}

		try {
			opener!({
				...options,
				onAction: () => settle(true),
				onCancel: () => settle(false),
				onClose: () => settle(false),
			})
		} catch (error) {
			// The provider throws if a dialog is already open. Treat an unanswerable question as unanswered.
			console.error("Could not open the alert dialog:", error)
			settle(false)
		}
	})
}
